/**
 * Carrito de compras en `localStorage`. Emite el evento `mb-cart-change` en
 * `window` cada vez que cambia, para que la UI (badge, página) se actualice.
 */
export interface CartItem {
  slug: string;
  nombre: string;
  precio: number;
  unidad?: string;
  imagen?: string;
  qty: number;
}

const KEY = "mb_cart";
const EVENT = "mb-cart-change";

const read = (): CartItem[] => {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const write = (items: CartItem[]) => {
  localStorage.setItem(KEY, JSON.stringify(items));
  window.dispatchEvent(new CustomEvent(EVENT, { detail: items }));
};

export const cart = {
  items: read,
  count: (): number => read().reduce((s, i) => s + i.qty, 0),
  total: (): number => read().reduce((s, i) => s + i.precio * i.qty, 0),

  add(item: Omit<CartItem, "qty"> & { qty?: number }): CartItem[] {
    const items = read();
    const qty = item.qty ?? 1;
    const found = items.find((i) => i.slug === item.slug);
    if (found) found.qty += qty;
    else items.push({ ...item, qty });
    write(items);
    return items;
  },

  setQty(slug: string, qty: number): CartItem[] {
    const items = read().map((i) =>
      i.slug === slug ? { ...i, qty: Math.max(1, Math.floor(qty) || 1) } : i
    );
    write(items);
    return items;
  },

  remove(slug: string): CartItem[] {
    const items = read().filter((i) => i.slug !== slug);
    write(items);
    return items;
  },

  clear(): void {
    write([]);
  },

  /** Suscribe un handler a los cambios del carrito; devuelve el unsubscribe. */
  subscribe(fn: (items: CartItem[]) => void): () => void {
    const handler = () => fn(read());
    window.addEventListener(EVENT, handler);
    return () => window.removeEventListener(EVENT, handler);
  },
};

export default cart;
